import React, { useState } from 'react';
import {
  X,
  Mic,
  Boxes,
  Zap,
  Search,
  Headphones,
  Activity,
  StopCircle,
  Sparkles,
  Clock,
  BookOpen,
  Bookmark,
  Layers,
  ChevronRight,
  Flame
} from 'lucide-react';
import { SessionGameType, SessionGameContentSource } from '../../../types/session-games';
import { GAME_OPTIONS } from '../../../services/sessionGameService';

interface GameSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLaunchGame: (gameType: SessionGameType, source: SessionGameContentSource) => void;
  materialTitle?: string;
  savedWordsCount: number;
  hasMaterial: boolean;
  partnerName: string;
}

export const GameSelectionModal: React.FC<GameSelectionModalProps> = ({
  isOpen,
  onClose,
  onLaunchGame,
  materialTitle,
  savedWordsCount,
  hasMaterial,
  partnerName
}) => {
  const [selectedSource, setSelectedSource] = useState<SessionGameContentSource>(
    hasMaterial ? 'news' : savedWordsCount > 0 ? 'saved_words' : 'thematic'
  );
  const [selectedGameType, setSelectedGameType] = useState<SessionGameType>('pronunciation_duel');

  if (!isOpen) return null;

  const renderIcon = (iconName: string) => {
    switch (iconName) {
      case 'Mic':
        return <Mic className="w-6 h-6 text-white" />;
      case 'Boxes':
        return <Boxes className="w-6 h-6 text-white" />;
      case 'Zap':
        return <Zap className="w-6 h-6 text-white" />;
      case 'Search':
        return <Search className="w-6 h-6 text-white" />;
      case 'Headphones':
        return <Headphones className="w-6 h-6 text-white" />;
      case 'Activity':
        return <Activity className="w-6 h-6 text-white" />;
      case 'StopCircle':
        return <StopCircle className="w-6 h-6 text-white" />;
      default:
        return <Sparkles className="w-6 h-6 text-white" />;
    }
  };

  const handleLaunch = (gameType: SessionGameType) => {
    onLaunchGame(gameType, selectedSource);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-3 md:p-6 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white rounded-3xl shadow-2xl border border-gray-100 w-full max-w-4xl max-h-[92vh] flex flex-col overflow-hidden">
        {/* Modal Header */}
        <div className="p-5 md:p-6 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-purple-50 via-indigo-50 to-pink-50">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-purple-600 to-indigo-600 text-white flex items-center justify-center shadow-md">
              <Sparkles className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-black text-gray-900 tracking-tight">
                Friend Games Arena
              </h2>
              <p className="text-xs md:text-sm text-gray-500 font-medium">
                Pick a multiplayer game to challenge and learn together with {partnerName}
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-700 hover:bg-white/80 rounded-xl transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content Source Selector Banner */}
        <div className="bg-gray-50/90 px-5 md:px-6 py-3 border-b border-gray-200/70 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-xs font-bold text-gray-700 uppercase tracking-wider">
            <Layers className="w-4 h-4 text-purple-600" />
            <span>Content Source:</span>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setSelectedSource('news')}
              className={`px-3 py-1.5 rounded-xl text-xs font-semibold flex items-center gap-1.5 transition-all ${
                selectedSource === 'news'
                  ? 'bg-purple-600 text-white shadow-xs'
                  : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-100'
              }`}
            >
              <BookOpen className="w-3.5 h-3.5" />
              <span>Current Material {hasMaterial ? '✓' : '(Fallback)'}</span>
            </button>

            <button
              onClick={() => setSelectedSource('saved_words')}
              className={`px-3 py-1.5 rounded-xl text-xs font-semibold flex items-center gap-1.5 transition-all ${
                selectedSource === 'saved_words'
                  ? 'bg-purple-600 text-white shadow-xs'
                  : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-100'
              }`}
            >
              <Bookmark className="w-3.5 h-3.5" />
              <span>Saved Words ({savedWordsCount})</span>
            </button>

            <button
              onClick={() => setSelectedSource('thematic')}
              className={`px-3 py-1.5 rounded-xl text-xs font-semibold flex items-center gap-1.5 transition-all ${
                selectedSource === 'thematic'
                  ? 'bg-purple-600 text-white shadow-xs'
                  : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-100'
              }`}
            >
              <Flame className="w-3.5 h-3.5 text-amber-500" />
              <span>Curated Thematic Pack</span>
            </button>
          </div>
        </div>

        {/* Games Grid */}
        <div className="flex-1 overflow-y-auto p-5 md:p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          {GAME_OPTIONS.map((game) => {
            const isSelected = selectedGameType === game.type;
            return (
              <div
                key={game.type}
                onClick={() => setSelectedGameType(game.type)}
                className={`p-4 md:p-5 rounded-2xl border-2 cursor-pointer transition-all flex flex-col justify-between group ${
                  isSelected
                    ? 'border-purple-600 bg-purple-50/30 shadow-md ring-2 ring-purple-500/20'
                    : 'border-gray-100 hover:border-purple-200 bg-white hover:shadow-sm'
                }`}
              >
                <div>
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-2xl bg-gradient-to-tr ${game.badgeColor} flex items-center justify-center shadow-sm`}>
                        {renderIcon(game.iconName)}
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-900 text-base leading-snug group-hover:text-purple-700 transition-colors">
                          {game.title}
                        </h3>
                        <div className="flex items-center gap-1.5 text-xs text-gray-400 mt-0.5">
                          <Clock className="w-3 h-3" />
                          <span>{game.estimatedDuration}</span>
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleLaunch(game.type);
                      }}
                      className="px-3.5 py-1.5 rounded-xl bg-purple-600 hover:bg-purple-700 text-white font-bold text-xs shadow-xs transition-transform active:scale-95 flex items-center gap-1"
                    >
                      <span>Play</span>
                      <ChevronRight className="w-3.5 h-3.5" />
                    </button>
                  </div>

                  <p className="text-xs text-gray-600 leading-relaxed mb-3">
                    {game.description}
                  </p>
                </div>

                {/* Skills tags */}
                <div className="pt-2 border-t border-gray-100/80 flex items-center gap-1.5 flex-wrap">
                  {game.skills.map((skill) => (
                    <span
                      key={skill}
                      className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-md bg-gray-100 text-gray-600 group-hover:bg-purple-100 group-hover:text-purple-700 transition-colors"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Modal Footer */}
        <div className="p-4 border-t border-gray-100 bg-gray-50 flex items-center justify-between text-xs text-gray-500">
          <span>Both friends in the room will automatically be joined to the game session.</span>
          <button
            onClick={() => handleLaunch(selectedGameType)}
            className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-bold text-sm shadow-md hover:opacity-95 transition-all"
          >
            Start {GAME_OPTIONS.find(g => g.type === selectedGameType)?.title || 'Game'} Now
          </button>
        </div>
      </div>
    </div>
  );
};
